import React, { useEffect, useState } from "react";
import * as Icon from "react-feather";
import { Button, Form, Input, InputNumber, Modal, Select, Switch, Tabs } from "antd";
import { useDispatch } from "react-redux";
import sliderService from "../../services/slider-service";
import appLoaderAction from "../../redux/action/app-loader-action";
import notifyUtitls from "../../utils/notify-utils";
import ContentSelect from "../../components/input/content-select";
import UploadImage from "../../components/upload/image";
import fileUtils from "../../utils/file-utils";
import VisibilityFormItem from "../../components/input/visibility-form-item";
import { LINK_TYPE, VISIBILITY_DEFAULT, groupType, fileTYPE_IMG } from "../../constants/app-constants";
import { FORM_LANG, formText, langOfTab } from "../../constants/form-i18n";

const { TabPane } = Tabs;

/**
 * NMU-473: add / edit a homepage slider card.
 *
 * The link half mirrors the Service Link form from NMU-470 — same wording, same
 * rule that only the field belonging to the chosen type is shown and required —
 * so an editor meets one concept, not two.
 */
const AddEditSliderDialog = ({ isShow, onCancel, onSaveComplete, editData }) => {
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [image, setImage] = useState(null);
  const [lang, setLang] = useState(FORM_LANG.TH);
  const t = formText(lang);

  useEffect(() => {
    if (!isShow) return;
    if (editData) {
      form.setFieldsValue({
        titleTH: editData.title?.titleTH,
        titleEN: editData.title?.titleEN,
        categoryTagTH: editData.categoryTagTH,
        categoryTagEN: editData.categoryTagEN,
        // Records saved before a type existed are URL cards.
        linkType: editData.linkType || (editData.templateId ? LINK_TYPE.CONTENT : LINK_TYPE.URL),
        templateId: editData.templateId,
        url: editData.url,
        seqNo: editData.seqNo,
        enableNewTab: !!editData.enableNewTab,
        status: !!editData.status,
        visibility: editData.visibility || VISIBILITY_DEFAULT,
      });
      setImage(editData.attachImage || null);
    } else {
      form.resetFields();
      setImage(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isShow, editData]);

  const onUploadTemp = ({ file }) => {
    if (file.status === "done") {
      const { bean } = file.response;
      setImage(bean);
    }
  };

  const onSave = async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch (err) {
      notifyUtitls.warning(t("warningTitle"), t("incompleteForm"));
      return;
    }

    // NMU-563: the poster is required only for a ลิงค์(URL) card. A เนื้อหา card
    // may leave it empty — the site then borrows the linked content's cover
    // image, and falls back to a placeholder if that content has none either.
    if (!image && values.linkType === LINK_TYPE.URL) {
      notifyUtitls.warning(t("warningTitle"), "กรุณาอัปโหลดรูปภาพประกอบสไลเดอร์");
      return;
    }

    try {
      setLoading(true);
      dispatch(appLoaderAction.enable());

      const bean = {
        ...(editData ? JSON.parse(JSON.stringify(editData)) : {}),
        title: { titleTH: values.titleTH, titleEN: values.titleEN },
        categoryTagTH: values.categoryTagTH,
        categoryTagEN: values.categoryTagEN,
        attachImage: image,
        linkType: values.linkType,
        // Clear the half that does not belong to the chosen type, so switching
        // type cannot leave a stale target behind (NMU-456's "เมนู02").
        templateId: values.linkType === LINK_TYPE.CONTENT ? values.templateId : null,
        url: values.linkType === LINK_TYPE.URL ? values.url : null,
        seqNo: values.seqNo === undefined || values.seqNo === null ? null : Number(values.seqNo),
        enableNewTab: !!values.enableNewTab,
        status: !!values.status,
        visibility: values.visibility,
      };

      if (editData) {
        await sliderService.edit(editData.sliderId, bean);
      } else {
        await sliderService.add(bean);
      }

      notifyUtitls.success(t("successTitle"), t("saveSuccess"));
      onSaveComplete();
    } catch (err) {
      notifyUtitls.error(err);
    } finally {
      dispatch(appLoaderAction.disale());
      setLoading(false);
    }
  };

  return (
    <Modal
      title={editData ? "แก้ไขสไลเดอร์" : "เพิ่มสไลเดอร์"}
      centered
      width={720}
      visible={isShow}
      onCancel={onCancel}
      destroyOnClose
      footer={[
        <Button key="submit" type="primary" onClick={onSave} loading={loading}>
          <Icon.Save className="me-2" size={17} />
          {t("save")}
        </Button>,
        <Button key="back" className="btn-gray" onClick={onCancel}>
          {t("cancel")}
        </Button>,
      ]}
    >
      <Form
        layout="vertical"
        form={form}
        preserve={false}
        initialValues={{ status: true, enableNewTab: false, visibility: VISIBILITY_DEFAULT }}
        validateMessages={{ required: t("required") }}
      >
        {/* forceRender for the same reason as the programme form: an unopened
            pane submits empty values under preserve={false}. */}
        <Tabs defaultActiveKey="1" onChange={(key) => setLang(langOfTab(key))}>
          <TabPane tab={t("tabThai")} key="1" forceRender>
            <Form.Item label="ชื่อหัวข้อสไลเดอร์ (ไทย)" name="titleTH" rules={[{ required: true }]}>
              <Input />
            </Form.Item>
            <Form.Item label="ป้ายกำกับหมวดหมู่ (ไทย)" name="categoryTagTH">
              <Input placeholder="เช่น สถิติความโปร่งใส" />
            </Form.Item>
          </TabPane>
          <TabPane tab={t("tabEnglish")} key="2" forceRender>
            <Form.Item label="Slider Title (EN)" name="titleEN">
              <Input />
            </Form.Item>
            <Form.Item label="Category / Badge (EN)" name="categoryTagEN">
              <Input />
            </Form.Item>
          </TabPane>
        </Tabs>

        {/* Same uploader the other modules use: it posts to the temp endpoint and
            hands back the stored bean, which is what gets saved on the card. */}
        {/* NMU-563: the asterisk follows the type — required for ลิงค์(URL),
            optional for เนื้อหา. */}
        <Form.Item
          noStyle
          shouldUpdate={(prev, cur) => prev.linkType !== cur.linkType}
        >
          {({ getFieldValue }) => (
        <Form.Item
          label="รูปภาพประกอบสไลเดอร์"
          required={getFieldValue("linkType") === LINK_TYPE.URL}
        >
          <UploadImage
            listType="picture-card"
            accept={fileTYPE_IMG}
            showUploadList={false}
            onChange={onUploadTemp}
            className={image ? "view-upload" : null}
          >
            {image ? (
              <img
                src={
                  image.tempFilename
                    ? fileUtils.getTempFileRenderUrl(image.tempFilename, image.type)
                    : fileUtils.getFileRenderUrl(
                        groupType.SLIDER,
                        editData ? editData.sliderId : null,
                        image.originalFilename,
                      )
                }
                alt="slider"
                style={{ width: "100%" }}
              />
            ) : (
              t("uploadFile")
            )}
          </UploadImage>
        </Form.Item>
          )}
        </Form.Item>

        <Form.Item label={t("linkType")} name="linkType" rules={[{ required: true }]}>
          <Select placeholder={t("linkTypePlaceholder")}>
            <Select.Option value={LINK_TYPE.CONTENT}>{t("linkTypeContent")}</Select.Option>
            <Select.Option value={LINK_TYPE.URL}>{t("linkTypeUrl")}</Select.Option>
          </Select>
        </Form.Item>

        <Form.Item noStyle shouldUpdate={(prev, cur) => prev.linkType !== cur.linkType}>
          {({ getFieldValue }) =>
            getFieldValue("linkType") === LINK_TYPE.CONTENT ? (
              <Form.Item label={t("contentField")} name="templateId" rules={[{ required: true }]}>
                <ContentSelect placeholder={t("contentPlaceholder")} />
              </Form.Item>
            ) : getFieldValue("linkType") === LINK_TYPE.URL ? (
              <Form.Item label={t("link")} name="url" rules={[{ required: true }]}>
                <Input />
              </Form.Item>
            ) : null
          }
        </Form.Item>

        <Form.Item label="ลำดับการแสดงผล" name="seqNo">
          <InputNumber min={1} style={{ width: "100%" }} />
        </Form.Item>

        <Form.Item label={t("openInNewTab")} name="enableNewTab" valuePropName="checked">
          <Switch />
        </Form.Item>

        <Form.Item label={t("status")} name="status" valuePropName="checked">
          <Switch />
        </Form.Item>

        <VisibilityFormItem lang={lang} />
      </Form>
    </Modal>
  );
};

export default AddEditSliderDialog;
